import { config } from '@/common/config';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import axios from 'axios';

import { AUTH_DATA_STORAGE_KEY } from '../../common/constants';
import { setAccessToken, setAuthLoading } from '../reducers/common.slice';
import { TypeRootState } from '../store';
import { AuthResponse, StorageAuthData } from './types.api';

export interface ILogoutResponseData {
  status: boolean;
}

export interface IRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

// export const API_URL = `${process.env.REACT_APP_API_URL}/`;
// export const API_URL = 'https://buh.astericapital.kg/bo_test/hs/ast/';

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: config.API_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as TypeRootState).common.accessToken;

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
      headers.set('accept', 'application/json, text/plain, */*');
    }

    return headers;
  },
});

const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  const isUnauthorizedError =
    result.error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((result.error as any)?.status === 401 ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result.error as any)?.originalStatus === 402);

  if (isUnauthorizedError) {
    if (!mutex.isLocked()) {
      const logout = async (tokensData?: AuthResponse): Promise<void> => {
        localStorage.removeItem(AUTH_DATA_STORAGE_KEY);
        api.dispatch(setAccessToken(null));
        api.dispatch(setAuthLoading(true));

        await axios(`${config.API_URL}logout`, {
          method: 'GET',
          headers: {
            authorization: `Bearer ${tokensData?.accessToken}`,
          },
        });
      };

      const release = await mutex.acquire();

      const userData = localStorage.getItem(AUTH_DATA_STORAGE_KEY);

      if (!userData) {
        logout();
        release();
        return result;
      }

      const parsedUserData: AuthResponse = JSON.parse(userData);

      try {
        const refreshRequestResponse = await axios(`${config.API_URL}refresh`, {
          method: 'GET',
          headers: {
            authorization: `Bearer ${parsedUserData.refreshToken}`,
          },
        });

        const refreshedTokens = refreshRequestResponse.data as IRefreshResponse;

        if (refreshedTokens) {
          api.dispatch(setAccessToken(refreshedTokens.accessToken));

          const storageAuthData: StorageAuthData = {
            accessToken: refreshedTokens.accessToken,
            refreshToken: refreshedTokens.refreshToken,
          };

          localStorage.setItem(
            AUTH_DATA_STORAGE_KEY,
            JSON.stringify(storageAuthData),
          );

          result = await baseQuery(args, api, extraOptions);
        } else {
          logout(parsedUserData);
        }
      } catch (err) {
        logout(parsedUserData);
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  tagTypes: ['userInfo'],
  baseQuery: baseQueryWithReAuth,
  endpoints: (builder) => ({
    // выход из всех устройств
    reset: builder.query<ILogoutResponseData, void>({
      query: () => ({
        url: 'auth/reset',
      }),
    }),
  }),
});
